// Plan 24b, Task 1.1 — Soul Anchor Claim dialog (Screen 13)
//
// Bottom-center dialog overlay shown when the local player lands on a Soul Anchor tile
// and must choose a toll type to claim it.
//
// Uses createButton for both choice buttons so button-states hover/pressed
// animations work and dispose() cleanup is reliable.

import type { ScreenHandle, ScreenMountContext } from './types';
import type { BoardMountPayload } from './board-payload';
import { createButton } from '../components/button';
import type { UIComponentHandle } from '../components/types';
import type { SoulAnchorMode } from '../../../shared/game/types';
import { deserializeGameState } from '@shared/protocol';
import type { ServerMessage } from '@shared/protocol';
import { createBoardTopTimer } from './board-timer';

// ─────────────────────────────────────────────────────────────────────────────
// Token constants — exact values from docs/design/tokens.json
// ─────────────────────────────────────────────────────────────────────────────

/** color.tile.soul = #40B0C0 */
const SOUL_CYAN = '#40B0C0';

/** color.text.primary = #FFFFFF */
const WHITE = '#FFFFFF';

/** color.text.secondary = #B0B8C8 */
const SECONDARY_TEXT = '#B0B8C8';

/** color.ui.panel-bg-alpha — using higher opacity for dialog legibility */
const DIALOG_BG = 'rgba(30, 35, 50, 0.92)';

/** border-radius.xl = 16px */
const RADIUS_XL = '16px';

/** typography.size.h3 = 28px */
const H3_SIZE = '28px';

/** typography.size.caption = 14px */
const CAPTION_SIZE = '14px';

/** typography.family.headline */
const HEADLINE_FAMILY = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif";

/** typography.family.body */
const BODY_FAMILY = "'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif";

/** Above the dev-only god-mode panel (10000) so the modal remains visible in smoke runs. */
const Z_INDEX_SOUL_ANCHOR_MODAL = '10050';
const Z_INDEX_SOUL_ANCHOR_HOST = '10050';

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createSoulAnchorScreen(ctx: ScreenMountContext<BoardMountPayload>): ScreenHandle {
  const { gsapRegistry, host, router, payload } = ctx;
  const previousHostZIndex = host.style.zIndex;

  let disposed = false;
  let latestState = payload.state;
  /** Prevents double-send if both buttons' click events fire or user clicks twice. */
  let choiceMade = false;
  let chosenChoice: SoulAnchorMode | null = null;
  let actionControlsLocked = payload.isActionControlLocked?.() ?? false;
  let closeTraceSent = false;
  let unsubscribe: (() => void) | null = null;
  let unsubscribeActionLock: (() => void) | null = null;

  /** Tracked button handles so dispose() can clean them up. */
  let stealKeysBtn: UIComponentHandle | null = null;
  let stealHpBtn: UIComponentHandle | null = null;

  host.style.zIndex = Z_INDEX_SOUL_ANCHOR_HOST;

  // ── Root overlay ───────────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.className = 'screen-soul-anchor';
  root.setAttribute('data-screen-id', 'soul-anchor');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    `z-index: ${Z_INDEX_SOUL_ANCHOR_MODAL}`,
    'display: flex',
    'align-items: flex-end',
    'justify-content: center',
    'pointer-events: none',
  ].join('; ');

  const boardTimer = createBoardTopTimer(payload, { subscribe: false });
  root.appendChild(boardTimer.element);

  // ── Dialog panel (bottom-center, ~400px wide) ─────────────────────────────

  const dialog = document.createElement('div');
  dialog.className = 'soul-anchor-dialog';
  dialog.style.cssText = [
    'width: 400px',
    'max-width: calc(100vw - 32px)',
    `background: ${DIALOG_BG}`,
    `border-radius: ${RADIUS_XL}`,
    'padding: 24px',
    'margin-bottom: 40px',
    'pointer-events: auto',
    'display: flex',
    'flex-direction: column',
    'align-items: center',
    'gap: 8px',
  ].join('; ');

  // ── Glyph row ──────────────────────────────────────────────────────────────

  const glyphEl = document.createElement('div');
  glyphEl.className = 'soul-anchor-glyph';
  glyphEl.textContent = '⚓';
  glyphEl.style.cssText = [
    'font-size: 32px',
    `color: ${SOUL_CYAN}`,
    'line-height: 1',
    'margin-bottom: 4px',
  ].join('; ');

  // ── Title ─────────────────────────────────────────────────────────────────

  const titleEl = document.createElement('div');
  titleEl.className = 'soul-anchor-title';
  titleEl.textContent = 'Soul Anchor!';
  titleEl.style.cssText = [
    `font-family: ${HEADLINE_FAMILY}`,
    `font-size: ${H3_SIZE}`,
    'font-weight: 700',
    `color: ${WHITE}`,
    'text-align: center',
    'margin-bottom: 2px',
  ].join('; ');

  // ── Subtitle ──────────────────────────────────────────────────────────────

  const subtitleEl = document.createElement('div');
  subtitleEl.className = 'soul-anchor-subtitle';
  subtitleEl.textContent = 'Claim this tile. Choose your toll.';
  subtitleEl.style.cssText = [
    `font-family: ${BODY_FAMILY}`,
    `font-size: ${CAPTION_SIZE}`,
    `color: ${SECONDARY_TEXT}`,
    'text-align: center',
    'margin-bottom: 12px',
  ].join('; ');

  // ── Button container ───────────────────────────────────────────────────────

  const buttonContainer = document.createElement('div');
  buttonContainer.className = 'soul-anchor-buttons';
  buttonContainer.style.cssText = [
    'width: 100%',
    'max-width: 420px',
    'display: flex',
    'flex-direction: column',
    'align-items: stretch',
    'gap: 14px',
  ].join('; ');

  // ── Claim handler (shared by both buttons) ─────────────────────────────────

  function handleClaim(choice: SoulAnchorMode): void {
    if (choiceMade || disposed || actionControlsLocked) return;
    choiceMade = true;
    chosenChoice = choice;
    setPending(true);
    payload.send({ type: 'claim_soul_anchor', choice });
  }

  // ── Steal Keys button (Primary) ─────────────────────────────────────────────

  const stealKeysWrapper = document.createElement('div');
  stealKeysWrapper.className = 'soul-anchor-choice soul-anchor-choice--keys';
  stealKeysWrapper.style.cssText = [
    'width: 100%',
    'display: flex',
    'flex-direction: column',
    'align-items: stretch',
  ].join('; ');

  stealKeysBtn = createButton({
    gsapRegistry,
    mount: stealKeysWrapper,
    variant: 'primary',
    label: 'Steal Keys',
    onClick: () => handleClaim('keys'),
  });
  stealKeysBtn.element.style.width = '100%';
  stealKeysBtn.element.style.minWidth = '0';

  // Subtext "(6 per visitor)"
  const stealKeysSubtext = document.createElement('div');
  stealKeysSubtext.className = 'soul-anchor-choice-subtext';
  stealKeysSubtext.textContent = '(6 per visitor)';
  stealKeysSubtext.style.cssText = [
    'width: 100%',
    'text-align: center',
    `font-family: ${BODY_FAMILY}`,
    `font-size: ${CAPTION_SIZE}`,
    'line-height: 1.2',
    `color: ${SECONDARY_TEXT}`,
    'margin-top: 8px',
    'overflow-wrap: anywhere',
  ].join('; ');
  stealKeysWrapper.appendChild(stealKeysSubtext);
  buttonContainer.appendChild(stealKeysWrapper);

  // ── Steal HP button (Danger) ─────────────────────────────────────────────────

  const stealHpWrapper = document.createElement('div');
  stealHpWrapper.className = 'soul-anchor-choice soul-anchor-choice--hp';
  stealHpWrapper.style.cssText = [
    'width: 100%',
    'display: flex',
    'flex-direction: column',
    'align-items: stretch',
  ].join('; ');

  stealHpBtn = createButton({
    gsapRegistry,
    mount: stealHpWrapper,
    variant: 'danger',
    label: 'Steal HP',
    onClick: () => handleClaim('hp'),
  });
  stealHpBtn.element.style.width = '100%';
  stealHpBtn.element.style.minWidth = '0';

  // Subtext "(9-12 per visitor)"
  const stealHpSubtext = document.createElement('div');
  stealHpSubtext.className = 'soul-anchor-choice-subtext';
  stealHpSubtext.textContent = '(9-12 per visitor)';
  stealHpSubtext.style.cssText = [
    'width: 100%',
    'text-align: center',
    `font-family: ${BODY_FAMILY}`,
    `font-size: ${CAPTION_SIZE}`,
    'line-height: 1.2',
    `color: ${SECONDARY_TEXT}`,
    'margin-top: 8px',
    'overflow-wrap: anywhere',
  ].join('; ');
  stealHpWrapper.appendChild(stealHpSubtext);
  buttonContainer.appendChild(stealHpWrapper);

  // ── Assemble DOM ───────────────────────────────────────────────────────────

  dialog.appendChild(glyphEl);
  dialog.appendChild(titleEl);
  dialog.appendChild(subtitleEl);
  dialog.appendChild(buttonContainer);
  root.appendChild(dialog);
  host.appendChild(root);

  unsubscribe = payload.subscribe((msg: ServerMessage) => {
    if (disposed) return;

    if (msg.type === 'event' && isSoulAnchorCompletionEvent(msg)) {
      closeToBoardHud('soul_anchor_claim');
      return;
    }

    if (msg.type === 'state_sync') {
      latestState = deserializeGameState(msg.state);
      boardTimer.updateFromGameState(latestState);
      if (isLocalSoulAnchorPrompt(msg)) return;
      closeToBoardHud('state_sync');
      return;
    }

    if (msg.type === 'notification' && isSoulAnchorChoiceRejection(msg.text)) {
      console.warn(`[soul-anchor] server rejected claim choice: ${msg.text}`);
      choiceMade = false;
      chosenChoice = null;
      setPending(false);
    }
  });
  unsubscribeActionLock =
    payload.subscribeActionControlLock?.((locked) => {
      if (disposed || actionControlsLocked === locked) return;
      actionControlsLocked = locked;
      setPending(choiceMade);
    }) ?? null;
  setPending(choiceMade);

  function closeToBoardHud(reason: string): void {
    traceModalClose(reason);
    const nextPayload: BoardMountPayload = {
      ...payload,
      state: latestState,
    };
    dispose();
    router.navigate('board-hud', nextPayload);
  }

  function traceModalClose(reason: string): void {
    if (closeTraceSent) return;
    closeTraceSent = true;
    payload.tracePresentation?.({
      stage: 'modal_close',
      screen: 'soul-anchor',
      nodeId:
        latestState.pendingSoulAnchorClaim?.nodeId ?? payload.state.pendingSoulAnchorClaim?.nodeId,
      reason,
    });
  }

  function setPending(pending: boolean): void {
    const locked = pending || actionControlsLocked;
    root.dataset.pending = pending ? 'true' : 'false';
    root.dataset.actionLocked = locked ? 'true' : 'false';
    for (const button of [stealKeysBtn?.element, stealHpBtn?.element]) {
      if (!(button instanceof HTMLButtonElement)) continue;
      button.disabled = locked;
      button.style.pointerEvents = locked ? 'none' : 'auto';
      button.style.opacity = locked ? '0.72' : '1';
      button.toggleAttribute('data-action-locked', locked);
    }
  }

  function isSoulAnchorCompletionEvent(msg: Extract<ServerMessage, { type: 'event' }>): boolean {
    const promptNodeId =
      payload.state.pendingSoulAnchorClaim?.nodeId ?? latestState.pendingSoulAnchorClaim?.nodeId;
    return (
      msg.event.type === 'soul_anchor_claim' &&
      msg.event.playerId === payload.localPlayerId &&
      (promptNodeId == null || msg.event.nodeId === promptNodeId) &&
      (chosenChoice == null || msg.event.mode == null || msg.event.mode === chosenChoice)
    );
  }

  function isLocalSoulAnchorPrompt(msg: Extract<ServerMessage, { type: 'state_sync' }>): boolean {
    return (
      msg.state.pendingSoulAnchorClaim?.playerId === payload.localPlayerId &&
      msg.state.pendingSoulAnchorClaim?.nodeId ===
        (payload.state.pendingSoulAnchorClaim?.nodeId ?? latestState.pendingSoulAnchorClaim?.nodeId)
    );
  }

  function isSoulAnchorChoiceRejection(text: string): boolean {
    return (
      text === 'no_room' ||
      text === 'not_your_turn' ||
      text === 'invalid_choice' ||
      text === 'no_pending_claim' ||
      text === 'claim_failed' ||
      text.startsWith('wrong_phase:')
    );
  }

  // ── dispose ────────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    boardTimer.dispose();
    unsubscribe?.();
    unsubscribe = null;
    unsubscribeActionLock?.();
    unsubscribeActionLock = null;

    // Dispose button handles
    stealKeysBtn?.dispose();
    stealHpBtn?.dispose();
    stealKeysBtn = null;
    stealHpBtn = null;

    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
    host.style.zIndex = previousHostZIndex;
  }

  return { element: root, dispose };
}
